import React, {
  useContext, useEffect, useState
} from 'react';
import classNames from 'classnames';
import useScrollRestoration from '@hooks/useScrollRestoration/useScrollRestoration';
import BoardItemModelNames from '@constants/boardItemTypes';
import AddToBoardModalContext from '@context/Modal/add-to-board-modal-context';
import CreateBoardModalContext from '@context/Modal/create-board-modal-context';
import CreateProjectModalContext from '@context/Modal/create-project-modal-context';
import MobileAppConversionBanner from '@components/shared/MobileAppConversionBanner/MobileAppConversionBanner';
import If from '@components/shared/conditional/If/If';
import DefaultTemplate from '@components/shared/templates/DefaultTemplate/DefaultTemplate';
import useInfiniteScroll from '@hooks/useInfiniteScroll/useInfiniteScroll';
import { useRouter } from 'next/router';
import DiscoverFeedContext from '@context/DiscoverFeed/discover-feed-context';
import UserContext from '@context/User/user-context';
import isBrowser from '@utils/browser/isBrowser';
import env from '@lib/envs/env';
import IDiscoverPage from '@models/Discover/i-discover-page';
import { IRoomType } from '@models/Room/i-room-type';
import LoginRegisterModalContext from '@context/Modal/login-register-modal-context';
import { useTheme } from '@components/theme';
import { useMaxBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import CreateButton from './components/CreateButton/CreateButton';
import ExploreHeader from './components/ExploreHeader';
import SubNav from './components/SubNav/SubNav';
import ForYou from './components/ForYou/ForYou';
import styles from './ExploreFeedStyles.module.scss';
import Results from './components/Results/Results';
import getMetaTags from './getMetaTags';

const { IOS_APP_URL_SCHEME_BASE } = env;

const HAVENLY_EXPLORE_SELECTED_TAB_STORAGE_KEY = 'HAVENLY_EXPLORE_SELECTED_TAB';

const TAB_FOR_YOU = 0;
const TAB_DISCOVER_FEED = 1;

const PAGE_SOURCE = 'interior_design_ideas';

interface IExploreFeedProps {
  discoverPage?: IDiscoverPage;
  readOnly: boolean;
  queryVariables: any;
  roomTypes: IRoomType[];
}

const ExploreFeed = ({
  discoverPage,
  readOnly,
  roomTypes,
}: IExploreFeedProps) => {
  const { user, savedAssetIds } = useContext(UserContext);
  const { open: openAddToBoard } = useContext(AddToBoardModalContext);
  const { open: openCreateBoard } = useContext(CreateBoardModalContext);
  const { open: openCreateProject } = useContext(CreateProjectModalContext);
  const { open: openLoginRegister } = useContext(LoginRegisterModalContext);
  const theme = useTheme();
  const router = useRouter();

  const handleAuthRequiredAction = (action: () => void) => {
    if (!user) {
      openLoginRegister({
        onRegisterSuccess: () => action(),
        onLoginSuccess: () => action(),
        source: PAGE_SOURCE,
        magicLinkRedirectUrl: router.asPath,
      });
    } else {
      action();
    }
  };

  const handleCreateBoard = () => {
    return handleAuthRequiredAction(() => {
      openCreateBoard({ source: PAGE_SOURCE });
    });
  };

  const handleCreateProject = () => {
    return handleAuthRequiredAction(() => {
      openCreateProject({ presetRoomTypeId: '18' });
    });
  };

  useScrollRestoration(router);

  const [navOpen, setNavOpen] = useState(false);
  const [unbounceBannerHeight, setUnbounceBannerHeight] = useState(0);
  const discoverTopicSelected = !!discoverPage?.name;

  const isMobile = useMaxBreakpoint('large-phone');

  // We only show the "For You" app on mobile, so we need a boolean to disable it
  const shouldDisplayForYou = isBrowser() && isMobile && !discoverTopicSelected;

  const {
    boards,
    posts,
    searchRoomType,
    searchStyle,
    loadingResults,
    fetchMore,
    refresh: refreshFeed,
  } = useContext(DiscoverFeedContext);

  // if filters changed, refresh the feed
  useEffect(() => {
    refreshFeed();
    setNavOpen(false);
  }, [
    searchRoomType,
    searchStyle,
    discoverPage,
  ]);

  // this guards against the edge case of no SSR initialBoards or initialProducts
  useEffect(() => {
    if (!boards.length) {
      refreshFeed();
    }
  }, []);

  /**
   * If user is coming directly to a filtered discover feed url
   * e.g. /interior-design-ideas?roomType=living-room
   * we want to default to discover feed
   * Otherwise
   * We default selected tab to 0 because if "For you" isn't present, the component isn't
   * rendered which pushes the discover feed to the leftmost side of the fake viewport used
   * for tabbing.
   */
  const [selectedTab, setSelectedTab] = useState(
    getDefaultTab(searchRoomType, searchStyle, discoverTopicSelected),
  );

  // Used to offset the element wrapping both "For you" and "Discover" tabs to slide between them.
  const [xOffset, setXOffset] = useState(0);

  // The scroll position on the "discover" side so we can offset "For You" to the same location
  const [discoverScrollPosition, setDiscoverScrollPosition] = useState(0);

  useInfiniteScroll({
    trackVariablesForQueryReset: [discoverPage],
    targetScrollOffset: 2400,
    fetchMore,
  });

  const openModalForAsset = (sourceBoard: any, asset: any) => {
    if (sourceBoard && asset) {
      handleAuthRequiredAction(() => {
        openAddToBoard({
          saveItemId: Number(asset.id),
          saveItemImageUrl: asset.url,
          saveItemType: BoardItemModelNames.ASSETS,
          sourceBoard,
          source: PAGE_SOURCE,
        });
      });
    }
  };

  // Grab the "base offset" for the tab wrapper (defined by which tab is active).
  const baseOffset = !isBrowser() || selectedTab === 0 ? 0 : -window.innerWidth;

  // If selected tab changes, reset offset then animate.
  useEffect(() => {
    setXOffset(baseOffset);
    sessionStorage.setItem(HAVENLY_EXPLORE_SELECTED_TAB_STORAGE_KEY, String(selectedTab));
  }, [selectedTab]);

  if (typeof document !== 'undefined') {
    // If body scroll position changes, reset discover scroll position.
    useEffect(() => {
      setDiscoverScrollPosition(document.body.scrollTop || document.documentElement.scrollTop);
    }, [
      document.body.scrollTop,
      document.documentElement.scrollTop,
      document.body.scrollLeft,
      document.documentElement.scrollLeft,
    ]);
  }

  // Set up some direct style modification objects
  let navWrapperStyles = {};
  let exploreWrapperStyles = {};

  if (shouldDisplayForYou) {
    if (selectedTab === TAB_FOR_YOU) {
      navWrapperStyles = {
        transform: `translateY(${discoverScrollPosition}px)`,
      };
    }

    exploreWrapperStyles = {
      transform: `translate(${xOffset}px, 0)`,
      zIndex: 0,
    };
  }

  const metaData = getMetaTags(router.query, router.asPath, discoverPage);

  const navProps = {
    isFixed: true,
    isTransparent: true,
  };
  const isNavBarVisible = !shouldDisplayForYou || selectedTab === TAB_DISCOVER_FEED;

  return (
    <DefaultTemplate
      navProps={navProps}
      canonicalLink={metaData.url}
      description={metaData.description}
      checkUnbounceBannerHeight={
        (height) => setUnbounceBannerHeight(height)
      }
      facebook={{
        site_name: 'Havenly',
        image: metaData.image,
        url: metaData.url,
        type: metaData.type,
        title: metaData.title,
        description: metaData.description,
      }}
      initializeFacebook
      keywords={metaData.keywords}
      title={metaData.title}
      className={theme.styles.Body}
    >
      <ExploreHeader
        roomType={ searchRoomType && searchRoomType[0] }
        styleType={ searchStyle && searchStyle[0] }
        discoverPage={ discoverPage }
        hideSubHeader={ shouldDisplayForYou && selectedTab === TAB_FOR_YOU }
        shouldDisplayForYou={shouldDisplayForYou}
        roomTypes={roomTypes}
        navOpen={navOpen}
        setNavOpen={setNavOpen}
        selectedTab={selectedTab}
      />
      <div
        className={ `${styles.NavWrapper} ${discoverTopicSelected && styles.DiscoverTopicSelected}` }
        style={ navWrapperStyles }
      >
        <If condition={shouldDisplayForYou}>
          <span>
            <SubNav
              selectedTab={ selectedTab }
              setSelectedTab={ setSelectedTab }
            />
          </span>
        </If>
      </div>

      <span>
        <div
          className={ classNames(
            styles.ExploreWrapper,
            {
              [styles.NavbarVisible]: isNavBarVisible
            },
            { [styles.isDoubleWidth]: shouldDisplayForYou },
          ) }
          style={ exploreWrapperStyles }
        >
          <If condition={ shouldDisplayForYou }>
            {/* This span is needed for weird sass className population bug */}
            <span>
              <div
                className={ styles.ForYou }
                style={{ top: discoverScrollPosition }}
              >
                <ForYou
                  onSaveAsset={ openModalForAsset }
                  savedAssets={ savedAssetIds }
                  setDiscoverScrollPosition={ setDiscoverScrollPosition }
                />
              </div>
            </span>
          </If>

          <div className={
            classNames(styles.Explore,
              { [styles.NavbarVisible]: !isNavBarVisible },
              { [styles.DiscoverTopicSelected]: discoverTopicSelected })
          }
          >
            <Results
              boards={ boards }
              posts={ posts }
              onSaveAsset={ openModalForAsset }
              readOnly={ readOnly }
              savedAssets={ savedAssetIds }
              loading={ loadingResults }
              discoverSlug={discoverPage?.slug}
            />
          </div>
        </div>
      </span>

      <If condition={ !readOnly && !navOpen }>
        <CreateButton
          isHidden={ shouldDisplayForYou && selectedTab === 0 }
          openCreateBoard={() => handleCreateBoard()}
          openNewProject={() => handleCreateProject()}
        />
      </If>

      <MobileAppConversionBanner
        iosDeeplinkPath={getIosDeeplinkPath(searchStyle, searchRoomType)}
        unbounceBannerHeight={unbounceBannerHeight}
      />
    </DefaultTemplate>
  );
};

function getIosDeeplinkPath(searchStyle: number[] = [], searchRoomType: number[] = []) {
  const iosDeeplinkPath = `${IOS_APP_URL_SCHEME_BASE}://havenly.com/explore/discover`;

  const queryParams = new URLSearchParams();

  if (searchStyle.length > 0) {
    queryParams.set('styleFilterIds', searchStyle.join(', '));
  }

  if (searchRoomType.length > 0) {
    queryParams.set('roomFilterIds', searchRoomType.join(', '));
  }

  const queryString = queryParams.toString();

  if (queryString) {
    return `${iosDeeplinkPath}?${queryString}`;
  }

  return iosDeeplinkPath;
}

export default ExploreFeed;

function getDefaultTab(searchRoomType: any[], searchStyle: any[], discoverTopicSelected: boolean) {
  if (discoverTopicSelected) {
    return TAB_DISCOVER_FEED;
  }

  const cachedTab =
    isBrowser() ? parseInt(sessionStorage.getItem(HAVENLY_EXPLORE_SELECTED_TAB_STORAGE_KEY) || '0', 10) : 0;
  if (Number.isFinite(cachedTab)) {
    return cachedTab;
  }

  if ((
    searchRoomType && searchRoomType.length > 0
  ) || (
    searchStyle && searchStyle.length > 0
  )) {
    return TAB_DISCOVER_FEED;
  }

  return TAB_FOR_YOU;
}
